import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { core } from '@/api/client';
import { useAppContext, useAsyncData } from '@/hooks';
import { Icon } from '@/components/icons';
import { C1ChartCard, C1Grid, C1Section, C1Skeleton, C1StatusList, C1Tile } from '@/components/c1';
import { isGroupFinance, isInstitutionAdmin } from '@/auth/session';
import { normalizeGroupAnalytics } from '@/api/platform';
import type { GroupAnalytics } from '@/types/platform';
import {
  AcademicOverviewPanel,
  BursarPanel,
  CounsellorPanel,
  ExamOfficerPanel,
  LibrarianPanel,
  ParentPanel,
  RegistrarPanel,
  StudentPanel,
  TeacherPanel,
  TransportPanel,
} from './rolePanels';
import { GroupAdminPanel } from './groupPanel';
import type { InsightsResult } from './types';

/**
 * Role-aware insight panels for the home page.
 *
 * THREE SOURCES, ON PURPOSE. A group administrator's figures span every member
 * school and come from the group dashboard. An institution administrator's
 * figures are CampusOne's own — campuses, people, subscriptions, integration
 * health — and come from the institution dashboard. Everyone else's are
 * academic, and the non-duplication map puts the academic record in EduCore,
 * so they come through `me/insights`, which resolves the caller's role
 * server-side and asks EduCore for an aggregate it computes beside its own
 * collections. Merging them into one endpoint would put half of EduCore's
 * meaning into CampusOne.
 *
 * WHAT IS DELIBERATELY NOT HERE: A TIME SERIES ON THE ADMINISTRATOR PANELS.
 * CampusOne records campuses and users as states, never as a history, so an
 * administrator's trend line would be drawn from numbers nobody measured.
 * Attendance and grades DO carry timestamps, which is why the teacher, student
 * and parent panels legitimately plot over time and these do not. The
 * distinction is the data's, not a stylistic one.
 *
 * FORM FOLLOWS THE QUESTION. Headline counts are stat tiles. Comparison across
 * a few categories is a horizontal bar from a common baseline. A figure with a
 * consequence attached — attendance against the eligibility threshold — is a
 * position against a marked rule, because the question is which side of the
 * line it falls on. Status is an icon and a word as well as a colour.
 *
 * THE PALETTE IS VALIDATED, NOT CHOSEN BY EYE. #0057D2 / #0D9E9E / #E9730E
 * clear the lightness band, chroma floor and CVD separation checks. The amber
 * sits below 3:1 against the surface, which obliges direct labels and a table
 * view rather than being waved through.
 */

interface DashboardData {
  campuses?: { total: number; active: number };
  users?: { total: number; active: number; pendingInvitations: number };
  products?: Array<{ code: string; name: string; status: string }>;
  academicYear?: { label: string; status: string; period: string } | null;
  pendingTasks?: Array<{ id: string; title: string; priority?: string }>;
  integrationHealth?: Array<{ service: string; status: string }>;
}

/** Validated categorical order. Assigned by position and never cycled. */
const SERIES = ['#0057D2', '#0D9E9E', '#E9730E'] as const;

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <C1Section className="insights" title={t('insights.title')}>
      {children}
    </C1Section>
  );
}

/**
 * The shape the panel is about to take, blocked out while it loads.
 *
 * WHY NOT THE WORD "LOADING". Every one of these panels resolves into a row of
 * tiles over a pair of charts, and a single line of text stands in for roughly
 * 500px of it. The page then jumps by that much when the figures land, which on a
 * home screen means whatever the reader had just moved the pointer towards is no
 * longer under it. Reserving the space costs nothing and the page stays still.
 *
 * ONE ANNOUNCEMENT, NOT THREE. Only the tile row speaks; the chart placeholders are
 * silent. Three live regions saying "loading" in sequence is three interruptions
 * that carry the same single fact.
 */
function InsightsLoading() {
  return (
    <Shell>
      <C1Skeleton variant="tile" count={4} />
      <div className="insight-charts">
        <C1Skeleton variant="chart" label={null} />
        <C1Skeleton variant="chart" label={null} />
      </div>
    </Shell>
  );
}

export function InsightsPanel() {
  const { activeContext } = useAppContext();
  const roleCode = activeContext?.role?.code;
  const institutionId = activeContext?.institution?.id;
  const groupId = activeContext?.group?.id;

  // The shared helper, not a local role comparison. It is documented as UI gating only —
  // the server still authorises every one of these reads — and keeping the check in one
  // place means a third admin role code is added once rather than found later.
  const administers = isInstitutionAdmin(roleCode);
  // The money seat: the finance manager and the president own the group's fee
  // figures; the group administrator's seat is withheld them, so the panel does
  // not offer the money it would only be able to render as dashes.
  const finance = isGroupFinance(roleCode);

  // Before the role check, not folded into it: a context that has not resolved yet is not
  // the same as a role without insights, and telling somebody "nothing here for your role"
  // while their session is still loading is a wrong answer that later becomes right.
  if (!activeContext) return null;

  // A group seat owns a group, not an institution — its home is the group's own dashboard,
  // not the role insight pipeline, which resolves a person against one school's record.
  return groupId ? (
    <GroupInsights groupId={groupId} canSeeFinance={finance} roleCode={roleCode} />
  ) : administers ? (
    <AdminInsights institutionId={institutionId} />
  ) : (
    <RoleInsights institutionId={institutionId} />
  );
}

/** CampusOne's own figures, for whoever runs the institution. */
function AdminInsights({ institutionId }: { institutionId?: string }) {
  const { t } = useTranslation();
  const { state } = useAsyncData<DashboardData>(
    `insights-admin-${institutionId ?? 'none'}`,
    () => core<DashboardData>(`/admin/institution/${institutionId}/dashboard`),
    Boolean(institutionId),
  );

  if (state.status === 'loading') return <InsightsLoading />;

  if (state.status === 'error') {
    return (
      <Shell>
        {/* Not an empty state. A failed read and a school with no data look identical
            otherwise, and reporting zero campuses to somebody who has four is worse than
            reporting nothing at all. */}
        <p className="insights-unavailable" role="alert">
          {t('insights.unavailable')}
        </p>
      </Shell>
    );
  }

  const data = state.data;
  const users = data.users ?? { total: 0, active: 0, pendingInvitations: 0 };
  const campuses = data.campuses ?? { total: 0, active: 0 };
  const liveProducts = (data.products ?? []).filter((p) => p.status === 'ACTIVE').length;
  const openTasks = (data.pendingTasks ?? []).length;

  // Composition, derived rather than assumed: whatever is neither active nor invited is
  // "other" instead of being dropped, so the bars always sum to the total a reader can
  // check against the tile above them.
  const other = Math.max(users.total - users.active - users.pendingInvitations, 0);
  const composition = [
    { key: 'active', label: t('insights.users.active'), value: users.active, color: SERIES[0] },
    { key: 'invited', label: t('insights.users.invited'), value: users.pendingInvitations, color: SERIES[1] },
    { key: 'other', label: t('insights.users.other'), value: other, color: SERIES[2] },
  ].filter((slice) => slice.value > 0);

  return (
    <Shell>
      <C1Grid min={200}>
        <StatTile label={t('insights.campuses')} value={campuses.active} sub={`/ ${campuses.total}`} icon="campus" to="/admin/institution/campuses" />
        <StatTile label={t('insights.people')} value={users.total} sub={`${users.pendingInvitations} ${t('insights.pending')}`} icon="users" to="/admin/institution/users" />
        <StatTile label={t('insights.products')} value={liveProducts} icon="products" to="/products" />
        <StatTile label={t('insights.openTasks')} value={openTasks} icon="tasks" to="/admin/institution/tasks" tone={openTasks > 0 ? 'warning' : undefined} />
      </C1Grid>

      <div className="insight-charts">
        <BarChart title={t('insights.users.title')} slices={composition} total={users.total} />
        <IntegrationHealth items={data.integrationHealth ?? []} />
      </div>
    </Shell>
  );
}

/**
 * Fetch layer for the group home: loads the group dashboard and hands the
 * normalized document to the render-only group panel.
 */
function GroupInsights({
  groupId,
  canSeeFinance,
  roleCode,
}: {
  groupId: string;
  canSeeFinance: boolean;
  roleCode?: string;
}) {
  const { t, i18n } = useTranslation();
  const { state } = useAsyncData<GroupAnalytics>(
    `insights-group-${groupId}`,
    () => core<unknown>(`/admin/group/${groupId}/dashboard`).then(normalizeGroupAnalytics),
    true,
  );

  if (state.status === 'loading') return <InsightsLoading />;

  if (state.status === 'error') {
    return (
      <Shell>
        {/* Not an empty state. A failed read and a group with no data look identical
            otherwise, and reporting zero schools to somebody who administers six is worse
            than reporting nothing at all. */}
        <p className="insights-unavailable" role="alert">
          {t('insights.unavailable')}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <GroupAdminPanel
        data={state.data}
        locale={i18n.language}
        canSeeFinance={canSeeFinance}
        roleCode={roleCode}
      />
    </Shell>
  );
}

/** Academic figures, for every other role, resolved server-side from the caller's context. */
function RoleInsights({ institutionId }: { institutionId?: string }) {
  const { t, i18n } = useTranslation();
  const { state } = useAsyncData<InsightsResult>(
    `insights-role-${institutionId ?? 'none'}`,
    () => core<InsightsResult>(`/me/insights?institutionId=${institutionId}`),
    Boolean(institutionId),
  );

  if (state.status === 'loading') return <InsightsLoading />;

  if (state.status === 'error') {
    return (
      <Shell>
        <p className="insights-unavailable" role="alert">
          {t('insights.unavailable')}
        </p>
      </Shell>
    );
  }

  const result = state.data;

  // Each state gets its own sentence. They are genuinely different situations and a reader
  // acts differently on each: UNLINKED is somebody to chase in onboarding, UNAVAILABLE is an
  // outage to report, UNSUPPORTED is simply not built yet. One shared "no data" message
  // would make all three indistinguishable — and would make an outage look like a design.
  if (result.state === 'UNAVAILABLE') {
    return (
      <Shell>
        <p className="insights-unavailable" role="alert">
          {t('insights.educoreUnavailable')}
        </p>
      </Shell>
    );
  }

  if (result.state === 'UNLINKED') {
    return (
      <Shell>
        <p className="insights-unavailable">{t('insights.unlinked')}</p>
      </Shell>
    );
  }

  if (result.state === 'UNSUPPORTED') {
    return (
      <Shell>
        <p className="insights-unavailable">{t('insights.notForRole')}</p>
      </Shell>
    );
  }

  const { insights } = result;
  const locale = i18n.language;

  return (
    <Shell>
      {insights.scope === 'TEACHER' && (
        <TeacherPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'STUDENT' && (
        <StudentPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'PARENT' && (
        <ParentPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'BURSAR' && (
        <BursarPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'LIBRARIAN' && (
        <LibrarianPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'REGISTRAR' && (
        <RegistrarPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'EXAM_OFFICER' && (
        <ExamOfficerPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'COUNSELLOR' && (
        <CounsellorPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'TRANSPORT' && (
        <TransportPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
      {insights.scope === 'ACADEMIC_OVERVIEW' && (
        <AcademicOverviewPanel data={insights} locale={locale} institutionId={institutionId} />
      )}
    </Shell>
  );
}

function StatTile({
  label, value, sub, icon, to, tone,
}: {
  label: string; value: number; sub?: string; icon: 'campus' | 'users' | 'products' | 'tasks';
  to: string; tone?: 'warning';
}) {
  // A link, not a card with an onClick: middle-click and "open in new tab" work, and the
  // destination is visible in the status bar before it is clicked.
  return (
    <C1Tile
      to={to}
      icon={<Icon name={icon} size={16} />}
      label={label}
      value={value}
      sub={sub}
      tone={tone}
    />
  );
}

/**
 * A horizontal bar, drawn in HTML rather than SVG.
 *
 * No chart library: three bars do not justify a dependency, and flexed divs reflow with the
 * page, inherit the type scale and stay legible when a browser zooms — none of which a
 * fixed-viewBox SVG does without extra work.
 *
 * Every bar is DIRECTLY LABELLED with its value. That is not decoration: the amber in this
 * palette sits below 3:1 against the surface, and the validator treats that as an
 * obligation to label rather than something to wave through. The table underneath is the
 * same data for anyone the bars do not serve.
 */
function BarChart({
  title, slices, total,
}: {
  title: string;
  slices: Array<{ key: string; label: string; value: number; color: string }>;
  total: number;
}) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  const max = Math.max(...slices.map((s) => s.value), 1);

  if (slices.length === 0) {
    return (
      <C1ChartCard title={title}>
        <p className="c1-chartEmpty">{t('insights.noPeople')}</p>
      </C1ChartCard>
    );
  }

  return (
    <C1ChartCard
      title={title}
      action={
        <button type="button" className="c1-tableToggle" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
          {showTable ? t('insights.showChart') : t('insights.showTable')}
        </button>
      }
    >
      {showTable ? (
        <table className="insight-table">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr><th scope="col">{t('insights.group')}</th><th scope="col">{t('insights.count')}</th></tr>
          </thead>
          <tbody>
            {slices.map((slice) => (
              <tr key={slice.key}><th scope="row">{slice.label}</th><td>{slice.value}</td></tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="insight-bars">
          {slices.map((slice) => (
            <li key={slice.key} className="insight-bar">
              <span className="insight-barLabel">{slice.label}</span>
              <span className="insight-barTrack">
                <span
                  className="insight-barFill"
                  style={{ width: `${(slice.value / max) * 100}%`, background: slice.color }}
                  // The bar is presentation; the row carries the meaning for a screen
                  // reader, so the number is announced once rather than twice.
                  aria-hidden="true"
                />
              </span>
              <span className="insight-barValue">
                {slice.value}
                <span className="sr-only"> {slice.label} {t('insights.ofTotal', { total })}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </C1ChartCard>
  );
}

/** Status is never colour alone: an icon and a word carry it too. */
function IntegrationHealth({ items }: { items: Array<{ service: string; status: string }> }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  const rows = items.map((item) => {
    const healthy = item.status === 'HEALTHY';
    const down = item.status === 'DOWN';
    return {
      id: item.service,
      icon: <Icon name={healthy ? 'checkmark' : down ? 'noAccess' : 'clock'} size={14} />,
      label: item.service,
      value: t(`insights.status.${item.status}`, item.status),
      tone: healthy ? ('good' as const) : down ? ('critical' as const) : ('warning' as const),
    };
  });

  return (
    <C1ChartCard title={t('insights.integrations')}>
      <C1StatusList items={rows} scroll={rows.length > 6} />
    </C1ChartCard>
  );
}
